import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Columns4, CheckCircle } from 'lucide-react';
import { subscriptionPlans, SubscriptionPlan } from '@/types/subscriptionPlans';
import PlanCard from '@/components/subscription/PlanCard';

const STORAGE_KEY = 'business_signup_plans';

const PricingPlans: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanType, setSelectedPlanType] = useState<'monthly' | 'quarterly'>('monthly');
  
  useEffect(() => {
    const savedPlans = localStorage.getItem(STORAGE_KEY);
    if (savedPlans) {
      try {
        setPlans(JSON.parse(savedPlans));
      } catch (error) {
        console.error('Error parsing saved business signup plans:', error);
        setPlans(subscriptionPlans);
      }
    } else {
      setPlans(subscriptionPlans);
    }
  }, []);

  return (
    <MainLayout isLoggedIn={false}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-blue mb-2">Pricing Plans</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select the right plan for your business needs. All plans include a 7-day trial.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 p-1 rounded-lg inline-flex">
            <button
              className={`px-4 py-2 rounded-md transition-all ${selectedPlanType === 'monthly' ? 'bg-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setSelectedPlanType('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-4 py-2 rounded-md transition-all ${selectedPlanType === 'quarterly' ? 'bg-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setSelectedPlanType('quarterly')}
            >
              Quarterly <span className="text-xs text-green-600">Save 15%</span>
            </button>
          </div>
        </div>

        <Card className="border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-primary-blue flex items-center justify-center gap-2">
              <Columns4 className="h-6 w-6" />
              ReferMe Subscription Options
            </CardTitle>
            <CardDescription className="text-center text-gray-600">
              All plans include a 7-day trial. No credit card required to start.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => (
                <PlanCard 
                  key={plan.id} 
                  plan={plan} 
                  selectedPlanId={null}
                  selectedPlanType={selectedPlanType}
                  onSelectPlan={() => {}}
                />
              ))}
            </div>
          </CardContent>
        </Card>
        
        <div className="mt-12 text-center bg-blue-50 rounded-lg p-8 shadow-md">
          <h2 className="text-2xl font-bold text-primary-blue mb-4">Ready to Supercharge Your Referral Program?</h2>
          <p className="text-lg text-gray-700 mb-6">
            Start your 7-day free trial and experience the power of our referral platform with zero commitment.
          </p>
          <Link to="/business-signup">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-10 py-3 text-lg rounded-full shadow-md transform transition-all hover:scale-105"
            >
              <CheckCircle className="mr-2 h-5 w-5" />
              Start Your Free Trial
            </Button>
          </Link>
          <p className="mt-4 text-sm text-gray-600">
            No credit card required. Cancel anytime.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default PricingPlans;
